import type { IsoTimestamp } from './common.js';
import type { ReportTemplate } from './reportTemplate.js';

export type AccountType =
  | 'brand'
  | 'group'
  | 'location'
  | 'head_office'
  | 'region'
  | 'dealership';

export type Industry =
  | 'fitness_franchise'
  | 'automotive_franchise'
  | 'small_business_ecommerce'
  | 'other';

/**
 * Human labels for every industry. ⚠️ A `Record<Industry, …>`, so a fifth
 * industry is a COMPILE ERROR here rather than a picker that silently omits it
 * (2026-09-23, the portal library audience picker — a hand-typed "Franchise,
 * Auto" list would have excluded Computer Alliance and everyone in `other`).
 */
export const INDUSTRY_LABELS: Record<Industry, string> = {
  fitness_franchise: 'Fitness franchise',
  automotive_franchise: 'Automotive franchise',
  small_business_ecommerce: 'Small business / ecommerce',
  other: 'Other',
};

/** Every industry, derived from the label record so the two cannot diverge. */
export const INDUSTRIES = Object.keys(INDUSTRY_LABELS) as Industry[];

export type AccountStatus = 'active' | 'paused' | 'cancelled';

export type MediaSpendPaidBy = 'client' | 'kyck';

export type Service =
  | 'google_ads'
  | 'meta_ads'
  // Phase 2b: `content` renamed to `content_creation` (migration +
  // read-normalise via src/lib/accountServices.ts normalizeService). Legacy
  // stored docs may still carry 'content' until the migration runs.
  | 'content_creation'
  | 'community_management'
  // Social Insights reporting gate (2026-07-24). A plain COMMERCIAL gate — who
  // shows in the /reporting/social account picker — with NO serviceConfig block.
  // The data prerequisite is the platform CONNECTION (Zernio syncs insights
  // automatically), not content creation or comment-syncing.
  | 'reporting'
  // Dealership advertising services (2026-07-24). Each reveals a config block
  // (serviceConfig.metaAia / .googleVla = { budgetCents, feedUrl }). Not
  // type-gated — only meaningful for dealerships, but any account may tick them.
  | 'meta_aia' // Meta Automated Inventory Ads
  | 'google_vla' // Google Vehicle Listing Ads
  | 'sms'
  | 'consulting'
  | 'email_marketing'
  | 'web_design';

// Phase 2b — structured per-service configuration on the Account. Each block is
// present only when its service is active (revealed on tick in the Services
// tab); the whole object is optional (legacy docs omit it → undefined). Money is
// integer cents (src/lib/money.ts) at the edges. Persisted via the accounts
// module's updateAccount (AUDITED), zod-validated at the router. Escalation
// contacts are `contactId` references into the contacts collection.
export type ServiceChannelToggles = {
  facebook: boolean;
  instagram: boolean;
  google: boolean;
  tiktok: boolean;
};

export type ContentCreationConfig = {
  channels: ServiceChannelToggles;
  quotas: {
    facebookFeed: number | null;
    instagramFeed: number | null;
    stories: number | null;
    googleBusiness: number | null;
  };
  mediaBudget: {
    facebookSpendCents: number | null;
    instagramSpendCents: number | null;
  };
  // Free-text contact blocks (address / phone / URL, emoji/format included) the
  // publishing composer's {sales_contact_details} / {service_contact_details}
  // caption wildcards resolve from. SPLIT 2026-08-05: the single
  // `contactDetailsForPost` became these two.
  //
  // BOTH exist for EVERY account type (not dealership-gated) — the wildcard set
  // has to be uniform, or a caption author faces a different token list depending
  // on the account. A gym simply leaves Service blank. (Deliberately unlike the
  // escalation-contact roles, which ARE type-varying: those are internal routing,
  // these are authoring vocabulary.)
  salesContactDetailsForPost: string;
  serviceContactDetailsForPost: string;
  /**
   * @deprecated Pre-2026-08-05 name for `salesContactDetailsForPost`. Present ONLY
   * on stored docs that predate the split — the billingAddress → address precedent.
   * `normalizeContentCreationConfig` folds its value into the sales field and
   * PRESERVES the key so the import migration can find and strip it. NEVER write
   * it: the wire schema's transform drops it, so any save makes the doc canonical.
   */
  contactDetailsForPost?: string;
  // Free-text content-sharing group, e.g. "WhatsApp" (2026-07-24). Legacy configs
  // predate it → read as '' via ccSliceFrom.
  contentSharingGroup: string;
  notes: string;
};

// The TOLERANT read/wire shape of the above: a stored doc (or a stale client)
// may carry only the legacy `contactDetailsForPost`, so both new fields are
// optional on the way IN. `normalizeContentCreationConfig` turns this into the
// canonical `ContentCreationConfig`.
export type ContentCreationConfigInput = Omit<
  ContentCreationConfig,
  'salesContactDetailsForPost' | 'serviceContactDetailsForPost'
> & {
  salesContactDetailsForPost?: string;
  serviceContactDetailsForPost?: string;
};

// Escalation contacts keyed by role. Which roles APPLY varies by account type
// (dealership → primary/sales/service; others → primary/backup — see
// escalationRolesForAccountType). All four keys exist in the shape; the
// inapplicable ones stay null. Values are `contactId` references (null = unset).
export type EscalationContacts = {
  primary: string | null;
  sales: string | null;
  service: string | null;
  backup: string | null;
};

export type CommunityManagementConfig = {
  channels: ServiceChannelToggles;
  // What we sign reviews / comments / DMs as, e.g. "The Team at Liverpool Mazda".
  signOffPerson: string;
  escalationContacts: EscalationContacts;
};

// Dealership advertising config (meta_aia / google_vla). Budget in integer cents
// (money.ts at the edges); feed URL is a plain URL string. Each block is present
// only when its service is ticked.
export type DealershipAdsConfig = {
  budgetCents: number | null;
  feedUrl: string | null;
};

export type ServiceConfig = {
  contentCreation?: ContentCreationConfig;
  communityManagement?: CommunityManagementConfig;
  metaAia?: DealershipAdsConfig;
  googleVla?: DealershipAdsConfig;
};

// The tolerant read/wire shape (see ContentCreationConfigInput) — what a STORED
// doc or a stale client may look like before normalizeStoredServiceConfig runs.
export type ServiceConfigInput = Omit<ServiceConfig, 'contentCreation'> & {
  contentCreation?: ContentCreationConfigInput;
};

export type Currency = 'AUD' | 'NZD' | 'USD' | 'GBP' | 'SGD';

export type Address = {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
};

// STORED, last-known per-platform API connection status (write-on-connect +
// manual refresh). NOT real-time — `lastCheckedAt` is when the booleans were
// last resolved from Zernio (null = never checked). Derived from the same
// profile→SocialAccounts resolution the api-connections endpoint uses, via the
// shared mapper in src/lib/accountApiConnections.ts. The whole block is null
// until the first finalize / detail-load / refresh populates it (no backfill).
export type AccountApiConnections = {
  metaAds: boolean;
  facebookPage: boolean;
  instagram: boolean;
  googleBusiness: boolean;
  googleAds: boolean;
  tiktok: boolean;
  lastCheckedAt: IsoTimestamp | null;
};

// Every Account carries its own currency. On create, the server defaults the
// field from the parent's currency when the caller omits it; on read, the
// field is always populated and edits are allowed at every level (mixed-
// currency trees are legal -- uncommon but real, e.g. an AU brand with an NZ
// subsidiary).
export type Account = {
  id: string;
  name: string;
  slug: string;
  parentAccountId: string | null;
  accountType: AccountType;
  industry: Industry;

  status: AccountStatus;
  statusChangedAt: IsoTimestamp | null;
  statusChangedBy: string | null;

  accountManagerId: string | null;
  mediaSpendPaidBy: MediaSpendPaidBy;
  salesforceAccountId: string | null;

  // General business phone for the account (2026-07-28). A public-facing number
  // usable in a post caption via the {phone} wildcard — deliberately NOT the
  // primary contact's number (the wrong thing to publish). Top-level (useful
  // beyond dealerships). Plain trimmed string; null when unset (legacy docs omit
  // it → read as null, no backfill).
  phone: string | null;

  // The Account's single structured address (renamed from `billingAddress`
  // 2026-07-24 — there's no separate physical address). Read-tolerant of the
  // legacy field name until the billing-address-to-address migration runs.
  address: Address | null;

  metaMasterMonthlyBudget: number | null;
  googleMasterMonthlyBudget: number | null;

  currency: Currency;

  // Authoritative operating country (reporting §3a). null until set; the
  // reporting account_dimensions sync reads this, seeding from a currency→country
  // map only when null. Free text (not enum-locked to today's AU/NZ wave).
  country: string | null;

  services: Service[];

  // Phase 2b — structured config for the services that have config (content
  // creation, community management). Optional (legacy docs omit it → undefined;
  // no backfill). Edited on the Services tab; each block appears only when its
  // service is ticked. Audited via updateAccount, zod-validated at the router.
  serviceConfig?: ServiceConfig;

  // Per-platform service flags (Pre-P2b-3 §2). Some clients run only Meta or
  // only Google; today this is implicit via $0 budget. Making it explicit lets
  // the rollout master grid dim the platform's columns and the Meta/Google
  // grids (P2b-3) skip these locations entirely. Default true for backward
  // compatibility — read sites use `acc.runsMeta ?? true` to tolerate legacy
  // docs without the field. AMs flip to false post-migration.
  runsMeta: boolean;
  runsGoogle: boolean;

  // Opt-in to the recurring reporting pull (Build A). The scheduled Cloud Run
  // Job reads this to decide which accounts to sync from Zernio into BigQuery.
  // Optional (legacy docs omit it → read as false; no backfill), same posture as
  // isSharedAdAccount. Default false — an account is NOT synced until explicitly
  // enabled. Read sites use `acc.reportingSyncEnabled ?? false`.
  reportingSyncEnabled?: boolean;

  // ⚠️⚠️ Opt-in to the RESTYLED client report (2026-08-30). Decides whether
  // THIS account's portal users see the new report or the existing one.
  // Optional (legacy docs omit it → read as FALSE; no backfill), same posture
  // as reportingSyncEnabled. Read sites use
  // `acc.portalReportRestyleEnabled ?? false`.
  //
  // ⚠️ IT EXISTS SO A ROLLBACK IS A TOGGLE, NOT A DEPLOY. Hard-wiring the
  // restyle would make undoing it a revert and a fifteen-minute deploy with a
  // client watching. Admin-gated, from the account page.
  //
  // ⚠️ A portal user's scope is a SET of accounts, and the report they see is
  // ONE report over all of them — so the resolution is ALL-OR-NOTHING: every
  // account in scope must have this on. A mixed scope falls back to the
  // existing report, which is the conservative direction and the one that
  // makes "default off" mean what it says.
  portalReportRestyleEnabled?: boolean;

  // ⚠️⚠️ The GOAL-SPLIT META REPORT (2026-09-24) — set on a BRAND account. Every
  // client-portal scope whose accounts ALL resolve (via computeBrandAncestorMap)
  // to a brand with this on gets Meta conversions split into four campaign-goal
  // panels instead of one combined figure. Keyed off the BRAND so a location
  // added beneath it inherits the report the day it is added — never a list of
  // account ids, never the brand's name. Optional (legacy docs omit it → read as
  // false; no backfill). Meaningful only on accountType 'brand'.
  portalMetaGoalReport?: boolean;

  // ⚠️⚠️ THE CLIENT REPORT TEMPLATE (2026-11-11) — staff-chosen, per account.
  // ABSENT / NULL = INHERIT (parent chain → marque → legacy tick → industry
  // default), which is NOT the same as 'standard'. Resolved by the ONE pure
  // resolver in src/lib/portalReport/reportTemplate.ts. Staff only; audited
  // through the ordinary account update.
  reportTemplate?: ReportTemplate | null;

  // Opt-in to Community Inbox ingestion (Phase 1 hardening). The webhook
  // receiver + reconcile poll only store comments for accounts with this on —
  // disabled accounts are dropped at the resolve step, so nothing is stored
  // for clients kyck doesn't community-manage. Optional (legacy docs omit it
  // → read as false; no backfill), same posture as reportingSyncEnabled.
  // Toggled via the Community page's admin panel (audited account edit).
  communityEnabled?: boolean;

  fbAdAccountId: string | null;
  fbAdAccountUrl: string | null;
  // §3b Shared Ad Accounts — campaign→location attribution. Several Kyck
  // Accounts can share ONE Meta ad account, and `getAdTree` returns the WHOLE
  // ad account's campaigns; these tokens attribute each campaign to THIS
  // location by case-insensitive substring match on the campaign name (resolved
  // at QUERY time in the reporting layer, §3b.4). `isSharedAdAccount` is only
  // the token-entry affordance — sharedness itself is ground truth (≥2 accounts
  // referencing one fbAdAccountId), computed at dimension sync. Optional
  // (legacy docs omit them → read as false / []; no backfill), same posture as
  // the Feature-A pixel/lead-form defaults.
  isSharedAdAccount?: boolean;
  adAccountMatchTokens?: string[];
  // Stage 7A — link to this Account's Zernio profile (the profile-per-client
  // model). Resolves to the facebook / instagram / metaads account ids via the
  // zernioProfiles cache at request time; kyck stores ONLY this id (not the
  // per-platform Zernio _ids). null until an operator links the Account.
  zernioProfileId: string | null;
  // "Meta connected at" — the timestamp of the FIRST successful onboarding
  // finalize that linked this account's Zernio profile (the Meta connect). Stamped
  // by the accounts service at finalize, FIRST-CONNECT-WINS: the stamp only lands
  // when it isn't already set. Server-managed (never set via the create/edit form
  // → excluded from AccountInput, same posture as apiConnections). null =
  // connected before this field existed (legacy, no backfill) or linked outside
  // the wizard.
  // ⚠️⚠️ IT ANSWERS "WHEN DID THIS CLIENT RELATIONSHIP START", NOT "DOES THIS
  // ACCOUNT HAVE AD HISTORY YET". The two coincide for a brand-new client and
  // diverge on a reconnect, and the reconnects differ:
  //   · A DEAD-TOKEN RE-AUTH (same ad account, new token) has nothing to backfill,
  //     so leaving the stamp alone is right.
  //   · A SWITCH TO A DIFFERENT AD ACCOUNT (drift fixed by re-scope, or a Reset
  //     connection + re-onboard) has NO history and MUST be backfilled — and this
  //     field still does not move. That is deliberate too: do NOT re-stamp it, and
  //     do NOT add an adAccountConnectedAt beside it. The coverage question is
  //     answered by the Coverage page from the data itself: rows are keyed by the
  //     normalised ad account id, so a new fbAdAccountId is a new row, and
  //     `needsFullBackfill` (coverageView.ts) flags it until the history is
  //     actually there. It also gets right the case a timestamp gets wrong — a
  //     "new" ad account that already has history from a shared pull is NOT
  //     flagged. Use Coverage's Backfill filter for "what needs a backfill"; this
  //     field only drives the "recently connected" recency sort.
  metaConnectedAt: IsoTimestamp | null;
  // DEMO CLUB (KyckConnect demo data, 2026-11-02). Set ONLY by `npm run demo:mark` (never through the account form or AccountInput):
  // it marks the one club that may be SEEDED with synthetic data. ⚠️ It must NEVER block anything: the demo club is also a live test
  // club. What it does: the seeder requires it, and Kyck staff rollups (Overview, all-clients Leads, staff Reporting) leave a demo club
  // out by default. Absent = a normal club.
  demoAccount?: boolean;
  // Stage 7A D2.11 — account-level default Instagram Business Account, used as the
  // ad's IG delivery identity (instagram_user_id). New Meta Ad drafts for this
  // account auto-populate `ad.instagramAccountId` from here (overridable per draft).
  // This is the IG account *ID* (e.g. 17841…), distinct from the `instagramUrl`
  // profile link above. null = let Meta auto-derive IG from the connected Page.
  defaultInstagramAccountId: string | null;
  defaultInstagramAccountName: string | null; // display label, e.g. @snapfitnessmtdruitt
  // Feature A — account-level Meta Ad Builder defaults captured at onboarding
  // (the pixel/lead-form picks). The builder pre-fills a NEW draft's pixel
  // selector + instant-form lead form from these (overridable per draft).
  // Optional (legacy/pre-Feature-A docs omit them → read as undefined/null via
  // `?? null`; no backfill). The default *Page* is intentionally NOT stored here
  // — it's already resolvable live from the Zernio profile's connected Page
  // (CampaignSection auto-selects the single connected Page), so a defaultPageId
  // would duplicate it.
  defaultPixelId?: string | null;
  // Denormalised display name for defaultPixelId (mirrors
  // defaultInstagramAccountName). Captured wherever the id is set (onboarding
  // finalize, account edit form) so the Accounts grid shows the pixel name
  // without a per-row Zernio lookup. null when no default pixel / name unknown.
  defaultPixelName?: string | null;
  defaultLeadFormId?: string | null;
  // Denormalised display name for defaultLeadFormId. Written by BOTH lead-form
  // default editors (Meta Setup → Forms star, account edit form dropdown) when
  // they set the id, via the shared updateAccount path. null when none / unknown.
  defaultLeadFormName?: string | null;
  googleAdsCustomerId: string | null;
  googleAdsUrl: string | null;

  // The client's own CRM identifier for this location (2026-08-31 — Snap
  // Fitness GymSales, `company_id`). It is the join key between a kyck account
  // and the club's rows in the brand's CRM table, and it is what puts leads,
  // sales and conversion rate on that club's client report.
  //
  // ⚠️ A STRING, even though Snap's `company_id` is a BigQuery INTEGER. Snap's
  // ids happen to be numeric; the next CRM's will not be, and a numeric field
  // would have to be widened later across the form, the schema and the query.
  // Coerced at the query boundary instead.
  //
  // ⚠️⚠️ IT DOES NOT ON ITS OWN LOCATE A CLUB'S DATA. Which TABLE to read is a
  // BRAND-level fact (Snap AU and Snap NZ are different tables) and lives in the
  // per-brand report registry, resolved from this account's `country`. Storing a
  // table name per account would be 330 copies of one string — 330 places for it
  // to be wrong, and a migration the next time a table is cut over. See
  // `resolvePortalCrmSource` in src/lib/portalReport/config.ts.
  //
  // null until linked (legacy docs omit it → read as null; the one-time fill is
  // scripts/backfill-account-crm-id.ts, which is dry-run by default).
  crmId: string | null;
  // The club's ClubWise code (Anytime Fitness; 2026-10-17) — the member-number
  // prefix, e.g. "ABC". ⚠️ SEPARATE FROM `crmId` ON PURPOSE: `crmId` is the
  // GymSales company id and is read by the Source Health grid, the CRM health /
  // onboarding panels and the backfill script, none of which know ClubWise. The
  // portal chooses WHICH of the two is the club key from the brand's CRM source
  // (`PortalCrmSource.clubKey`), never `crmId ?? clubwiseCode`.
  // Optional on the type (legacy docs omit it → read as null).
  clubwiseCode?: string | null;
  googleAnalyticsPropertyId: string | null;
  // GA4 CONNECTION (2026-11-12). The GA4 property whose site traffic is THIS
  // account's, pulled by the GA4 job and shown on its Website report. ⚠️ SERVER-
  // MANAGED: written only by connectGa4Property / disconnectGa4Property (audited,
  // one property → one account), never through the generic account form. Distinct
  // from the legacy free-text `googleAnalyticsPropertyId` above, which the GA4
  // pull does not read. Optional: legacy docs omit all three → null.
  ga4PropertyId?: string | null;
  ga4ConnectedAt?: string | null; // ISO
  ga4ConnectedBy?: string | null; // staff email
  facebookPageUrl: string | null;
  instagramUrl: string | null;
  tiktokUrl: string | null;
  googleBusinessProfileUrl: string | null;
  websiteUrl: string | null;
  reportUrl: string | null;
  clientDriveUrl: string | null;
  internalDriveUrl: string | null;

  // Tracking / integration references (2026-07-24, all account types). Optional
  // (legacy docs omit them → read as null via `?? null`; no backfill).
  gtmContainerId: string | null; // GTM container, e.g. GTM-N2NMD67N
  trackingDocUrl: string | null; // tracking documentation link
  zapierFolderUrl: string | null; // Zapier folder link

  // Meta verified advertiser (Verified identity) ID, 2026-10-23. A STRING of digits
  // (no precision loss in Firestore/JSON); sent to Meta as a number, as BOTH
  // universal_beneficiary and universal_payer, when the ad set targets Australia.
  // Optional: absent = null, no backfill.
  metaVerifiedAdvertiserId?: string | null;

  // Dealership fields (2026-07-24) — flat on the Account (only a few fields; keeps
  // brandAccountId cheaply readable by the brand resolver). Populated for
  // accountType==='dealership'; null elsewhere. brandAccountId is a cross-cutting
  // reference to a `brand`-type account (the marque, e.g. Mazda) — a dealership
  // belongs to a Group (its parent) AND a Brand (this explicit ref). The brand
  // resolver (computeBrandAncestorMap) gives a non-null brandAccountId precedence
  // over the parent-ancestor walk, so a dealership resolves to its marque across
  // every brand surface with no per-surface change.
  brandAccountId: string | null;
  dealershipWebsitePlatform: string | null; // client website CMS (Total Dealer / …)
  dealershipCrmSystem: string | null; // client CRM (Ezitrack / Autogate / …)

  description: string | null;
  targetingNotes: string | null;
  notes: string | null;
  leadSyncingNotes: string | null;

  // Xero integration (Phase 1 — account revenue). The Xero contact this
  // account's invoiced revenue attaches to, plus the org (tenant) that contact
  // lives in. Set via the Xero contact-mapping UI as an ordinary AUDITED account
  // edit (through updateAccount). Optional (legacy docs omit them → read as null
  // via `?? null`; no backfill), same posture as the reporting/onboarding flags.
  // Both must be set together (a contact id is only unique within its tenant).
  xeroContactId?: string | null;
  xeroTenantId?: string | null;

  // Last-known per-platform API connection status (STORED). null = never
  // checked. Server-derived (write-on-connect + manual refresh) — never set via
  // the create/edit form, so it's excluded from AccountInput below.
  apiConnections: AccountApiConnections | null;

  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
};

export type AccountInput = Omit<
  Account,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'statusChangedAt'
  | 'statusChangedBy'
  | 'apiConnections'
  | 'metaConnectedAt'
  | 'demoAccount'
>;

/**
 * The lean slice of Account the external-link icons need (website, Facebook
 * page, Instagram, Google Business profile, Google Ads, Meta ad account) — also the wire
 * shape carried on PublishingAccount, which is why it is not the whole Account.
 * See src/lib/accountLinks.ts.
 */
export interface AccountExternalLinks {
  websiteUrl: string | null;
  facebookPageUrl: string | null;
  instagramUrl: string | null;
  googleBusinessProfileUrl: string | null;
  googleAdsUrl: string | null;
  fbAdAccountUrl: string | null;
}
